/**
 * Data Mapper Utility
 * 
 * Handles mapping between CSV column headers and application fields
 * Includes automatic recognition and value transformation
 */

import { PLACEHOLDER_VALUES } from './validator';

// Value correction map for common case variations and misspellings
const VALUE_CORRECTIONS = {
  // Risk Sources corrections (PRD v8 update)
  'internal': 'Internal',
  'INTERNAL': 'Internal',
  'Internal': 'Internal',
  'inside': 'Internal',
  'INSIDE': 'Internal',
  'in-house': 'Internal',
  'IN-HOUSE': 'Internal',
  'inhouse': 'Internal',
  'INHOUSE': 'Internal',
  'internal source': 'Internal',
  'INTERNAL SOURCE': 'Internal',
  
  'external': 'External',
  'EXTERNAL': 'External',
  'External': 'External',
  'outside': 'External',
  'OUTSIDE': 'External',
  'third-party': 'External',
  'THIRD-PARTY': 'External',
  'third party': 'External',
  'THIRD PARTY': 'External',
  'vendor': 'External',
  'VENDOR': 'External',
  'supplier': 'External',
  'SUPPLIER': 'External',
  'external source': 'External',
  'EXTERNAL SOURCE': 'External',
  'outsourced': 'External',
  'OUTSOURCED': 'External',
  
  'both': 'Both',
  'BOTH': 'Both',
  'Both': 'Both',
  'internal and external': 'Both',
  'INTERNAL AND EXTERNAL': 'Both',
  'internal & external': 'Both',
  'INTERNAL & EXTERNAL': 'Both',
  'mixed': 'Both',
  'MIXED': 'Both',
  'hybrid': 'Both',
  'HYBRID': 'Both',
  'combination': 'Both',
  'COMBINATION': 'Both',
  'internal/external': 'Both',
  'INTERNAL/EXTERNAL': 'Both',
  
  // Criticality corrections
  '1-critical': '1-Extremely',
  '1-CRITICAL': '1-Extremely',
  '1-extremely': '1-Extremely',
  '1-EXTREMELY': '1-Extremely',
  'extremely': '1-Extremely',
  'EXTREMELY': '1-Extremely',
  'critical': '1-Extremely',
  'CRITICAL': '1-Extremely',
  'extremely critical': '1-Extremely',
  'EXTREMELY CRITICAL': '1-Extremely',
  '1': '1-Extremely',
  
  // Add space normalization for criticality levels
  '1- Extremely': '1-Extremely',
  '1 -Extremely': '1-Extremely', 
  '1 - Extremely': '1-Extremely',
  '1- Critical': '1-Extremely',
  '1 -Critical': '1-Extremely',
  '1 - Critical': '1-Extremely',
  
  '2-very high': '2-Very High',
  '2-VERY HIGH': '2-Very High',
  '2-veryhigh': '2-Very High',
  'very high': '2-Very High',
  'VERY HIGH': '2-Very High',
  'high critical': '2-Very High',
  'HIGH CRITICAL': '2-Very High',
  '2': '2-Very High',
  
  // Add space normalization for very high
  '2- Very High': '2-Very High',
  '2 -Very High': '2-Very High',
  '2 - Very High': '2-Very High',
  '2- High': '2-Very High',
  '2 -High': '2-Very High', 
  '2 - High': '2-Very High',
  
  '3-high': '3-High',
  '3-HIGH': '3-High',
  'high': '3-High',
  'HIGH': '3-High',
  '3': '3-High',
  
  // Add space normalization for high - this is the specific issue case
  '3- High': '3-High',
  '3 -High': '3-High',
  '3 - High': '3-High',
  
  '4-medium': '4-Medium',
  '4-MEDIUM': '4-Medium',
  'medium': '4-Medium',
  'MEDIUM': '4-Medium',
  'moderate': '4-Medium',
  'MODERATE': '4-Medium',
  '4': '4-Medium',
  
  // Add space normalization for medium
  '4- Medium': '4-Medium',
  '4 -Medium': '4-Medium',
  '4 - Medium': '4-Medium',
  
  '5-low': '5-Low',
  '5-LOW': '5-Low',
  'low': '5-Low',
  'LOW': '5-Low',
  'minimal': '5-Low',
  'MINIMAL': '5-Low',
  '5': '5-Low',
  
  // Add space normalization for low
  '5- Low': '5-Low',
  '5 -Low': '5-Low',
  '5 - Low': '5-Low',
  
  // Yes/No/Unknown variations
  'yes': 'Yes',
  'YES': 'Yes',
  'y': 'Yes',
  'Y': 'Yes',
  'true': 'Yes',
  'TRUE': 'Yes',
  'True': 'Yes',
  '1': 'Yes',
  'implemented': 'Yes',
  'IMPLEMENTED': 'Yes',
  'enabled': 'Yes',
  'ENABLED': 'Yes',
  'active': 'Yes',
  'ACTIVE': 'Yes',
  'present': 'Yes',
  'PRESENT': 'Yes',
  'available': 'Yes',
  'AVAILABLE': 'Yes',
  'deployed': 'Yes',
  'DEPLOYED': 'Yes',
  
  'no': 'No',
  'NO': 'No',
  'n': 'No',
  'N': 'No',
  'false': 'No',
  'FALSE': 'No',
  'False': 'No',
  '0': 'No',
  'not implemented': 'No',
  'NOT IMPLEMENTED': 'No',
  'disabled': 'No',
  'DISABLED': 'No',
  'inactive': 'No',
  'INACTIVE': 'No',
  'absent': 'No',
  'ABSENT': 'No',
  'missing': 'No',
  'MISSING': 'No',
  'not available': 'No',
  'NOT AVAILABLE': 'No',
  'not deployed': 'No',
  'NOT DEPLOYED': 'No',
  
  'unknown': 'Unknown',
  'UNKNOWN': 'Unknown',
  'Unknown': 'Unknown',
  '?': 'Unknown',
  'tbd': 'Unknown',
  'TBD': 'Unknown',
  'to be determined': 'Unknown',
  'TO BE DETERMINED': 'Unknown',
  'unclear': 'Unknown',
  'UNCLEAR': 'Unknown',
  'undetermined': 'Unknown',
  'UNDETERMINED': 'Unknown',
  
  // N/A variations
  'n/a': 'N/A',
  'N/a': 'N/A',
  'N/A': 'N/A',
  'na': 'N/A',
  'NA': 'N/A',
  'not applicable': 'N/A',
  'NOT APPLICABLE': 'N/A',
  'does not apply': 'N/A',
  'DOES NOT APPLY': 'N/A',
  'not relevant': 'N/A'
};

/**
 * Normalize spaces around hyphens for values like "3- High" -> "3-High"
 * @param {string} value - Input value to normalize
 * @returns {string} Normalized value
 */
const normalizeHyphenatedValues = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  // Normalize spaces around hyphens for patterns like "3- High" -> "3-High"
  return value.trim().replace(/\s*-\s*/g, '-');
};

/**
 * Correct common value variations and case issues
 * @param {string} value - Input value to correct
 * @returns {string} Corrected value
 */
const correctValue = (value) => {
  if (!value || value === '') return value;
  
  let correctedValue = String(value).trim();
  
  // Apply hyphen normalization first
  correctedValue = normalizeHyphenatedValues(correctedValue);
  
  // Then apply manual corrections
  correctedValue = VALUE_CORRECTIONS[correctedValue] || correctedValue;
  
  return correctedValue;
};

// Column mapping configuration - maps CSV headers to internal field names
export const CSV_FIELD_MAPPING = {
  // Enhanced Asset Name mapping - comprehensive variations
  'Application / system name': 'assetName',
  'Application/system name': 'assetName',
  'Application / System name': 'assetName',
  'Application/System name': 'assetName',
  'Application / System Name': 'assetName',
  'Application/System Name': 'assetName',
  'Application name': 'assetName',
  'Application Name': 'assetName',
  'System name': 'assetName',
  'System Name': 'assetName',
  'Asset name': 'assetName',
  'Asset Name': 'assetName',
  'Name': 'assetName',
  'APPLICATION / SYSTEM NAME': 'assetName',
  'APPLICATION/SYSTEM NAME': 'assetName',
  'APPLICATION NAME': 'assetName',
  'SYSTEM NAME': 'assetName',
  'APPLICATION': 'assetName',
  'SYSTEM': 'assetName',
  'Application': 'assetName',
  'System': 'assetName',
  'App Name': 'assetName',
  'App name': 'assetName',
  'Application Title': 'assetName',
  'System Title': 'assetName',
  
  'Description': 'assetDescription',
  'Asset Description': 'assetDescription',
  'Details': 'assetDescription',
  'Asset Details': 'assetDescription',
  'System Description': 'assetDescription',
  'Application Description': 'assetDescription',
  
  'Location': 'assetLocation',
  'Asset Location': 'assetLocation',
  'Physical Location': 'assetLocation',
  'Server Location': 'assetLocation',
  'Data Center': 'assetLocation',
  'Hosting Location': 'assetLocation',
  
  // Owner Information
  'Asset Owner Department': 'ownerDepartment',
  'Owner Department': 'ownerDepartment',
  'Department': 'ownerDepartment',
  'Business Unit': 'ownerDepartment',
  'Owning Department': 'ownerDepartment',
  'Responsible Department': 'ownerDepartment',
  'Business Owner Department': 'ownerDepartment',
  
  'Asset Owner Name': 'ownerName',
  'Owner Name': 'ownerName',
  'Contact Name': 'ownerName',
  'Business Owner': 'ownerName',
  'Asset Owner': 'ownerName',
  'Primary Contact': 'ownerName',
  'Responsible Person': 'ownerName',
  'Business Contact': 'ownerName',
  
  'E-Mail': 'ownerEmail',
  'Email': 'ownerEmail',
  'E-mail': 'ownerEmail',
  'Contact Email': 'ownerEmail',
  'Owner Email': 'ownerEmail',
  'Business Owner Email': 'ownerEmail',
  'Primary Contact Email': 'ownerEmail',
  'Email Address': 'ownerEmail',
  
  // Risk Sources mapping (PRD v8 update)
  'Risk Sources': 'riskSources',
  'Risk Source': 'riskSources', 
  'Source of Risk': 'riskSources',
  'Risk Origin': 'riskSources',
  'Threat Source': 'riskSources',
  'Risk Type': 'riskSources',
  'Source Type': 'riskSources',
  'Risk Classification': 'riskSources',
  'Threat Origin': 'riskSources',
  'Security Source': 'riskSources',
  
  'Criticality Classification': 'criticalityClassification',
  'Criticality': 'criticalityClassification',
  'Business Criticality': 'criticalityClassification',
  'Risk Level': 'criticalityClassification',
  'Criticality Level': 'criticalityClassification',
  'Business Impact': 'criticalityClassification',
  'Priority Level': 'criticalityClassification',
  
  // NEW: Consolidated Data Classification mapping
  'Contains Sensitive Data': 'containsSensitiveData',
  'Sensitive Data Type': 'containsSensitiveData',
  'Data Types': 'containsSensitiveData',
  'Information Types': 'containsSensitiveData',
  'PII Types': 'containsSensitiveData',
  'Sensitive Information': 'containsSensitiveData',
  'Information Classification': 'containsSensitiveData',
  'Data Content Type': 'containsSensitiveData',
  'Contains PII': 'containsSensitiveData',
  'Contains Sensitive Information': 'containsSensitiveData',
  
  // Legacy Data Classification mappings (for backward compatibility)
  'Customer PII': 'customerPII',
  'Customer Personal Data': 'customerPII',
  'Customer Information': 'customerPII',
  'Customer Data': 'customerPII',
  'Personal Customer Info': 'customerPII',
  
  'Employee PII': 'employeePII',
  'Employee Personal Data': 'employeePII',
  'Staff Information': 'employeePII',
  'Employee Data': 'employeePII',
  'Personnel Information': 'employeePII',
  'Staff Data': 'employeePII',
  
  'Sensitive Employee PII': 'sensitivePII',
  'Sensitive Personal Data': 'sensitivePII',
  'Sensitive Employee Data': 'sensitivePII',
  'Highly Sensitive PII': 'sensitivePII',
  
  'Board information': 'boardInfo',
  'Board Information': 'boardInfo',
  'Board Data': 'boardInfo',
  'Board Level Information': 'boardInfo',
  'Executive Board Data': 'boardInfo',
  
  'Executive Information': 'executiveInfo',
  'Executive Data': 'executiveInfo',
  'C-Level Information': 'executiveInfo',
  'Executive Level Data': 'executiveInfo',
  'Senior Management Info': 'executiveInfo',
  
  'Financial Information': 'financialInfo',
  'Financial Data': 'financialInfo',
  'Finance Information': 'financialInfo',
  'Financial Records': 'financialInfo',
  'Accounting Data': 'financialInfo',
  'Budget Information': 'financialInfo',
  
  'Other Confidential Information': 'otherConfidential',
  'Other Confidential': 'otherConfidential',
  'Additional Confidential Data': 'otherConfidential',
  'Other Sensitive Information': 'otherConfidential',
  'Proprietary Information': 'otherConfidential',
  
  // Streamlined Key Controls (6 controls only)
  'SSO': 'ssoImplemented',
  'Single Sign-On': 'ssoImplemented',
  'SSO Implemented': 'ssoImplemented',
  'Single Sign On': 'ssoImplemented',
  'SSO Implementation': 'ssoImplemented',
  
  '2FA': 'twoFactorAuth',
  'Two-Factor Authentication': 'twoFactorAuth',
  'Multi-Factor Authentication': 'twoFactorAuth',
  'MFA': 'twoFactorAuth',
  'Two Factor Auth': 'twoFactorAuth',
  'Multifactor Authentication': 'twoFactorAuth',
  '2-Factor Authentication': 'twoFactorAuth',
  
  'Data Encryption': 'dataEncryption',
  'Encryption': 'dataEncryption',
  'Data Protection': 'dataEncryption',
  'Encryption Implementation': 'dataEncryption',
  'Data Encryption at Rest': 'dataEncryption',
  'Encryption at Rest': 'dataEncryption',
  
  'Backups': 'backupsImplemented',
  'Backup Implemented': 'backupsImplemented',
  'Data Backup': 'backupsImplemented',
  'Backup Strategy': 'backupsImplemented',
  'Off-line / immutable backups, proven data restoration capability within RPO': 'backupsImplemented',
  'Backup and Recovery': 'backupsImplemented',
  'Data Recovery': 'backupsImplemented',
  'Backup Solution': 'backupsImplemented',
  'Immutable Backups': 'backupsImplemented',
  'Offline Backups': 'backupsImplemented',
  
  'Network Segmentation': 'networkSegmentation',
  'Network Isolation': 'networkSegmentation',
  'Segmentation': 'networkSegmentation',
  'Network Separation': 'networkSegmentation',
  'VLAN Segmentation': 'networkSegmentation',
  
  'User Access Reviews': 'userAccessReviews',
  'Access Reviews': 'userAccessReviews',
  'Periodic Reviews': 'userAccessReviews',
  'Access Review Process': 'userAccessReviews',
  'User Access Review': 'userAccessReviews',
  'Access Certification': 'userAccessReviews',
  
  // Legacy Key Controls mappings (for backward compatibility - removed from new form)
  'RBAC': 'rbacImplemented',
  'Role-Based Access Control': 'rbacImplemented',
  'RBAC Implemented': 'rbacImplemented',
  'Role Based Access': 'rbacImplemented',
  'Access Control': 'rbacImplemented',
  'Role Management': 'rbacImplemented',
  
  'Advanced Endpoint Protection': 'advancedEndpointProtection',
  'Endpoint Protection': 'advancedEndpointProtection',
  'EDR': 'advancedEndpointProtection',
  'AEP': 'advancedEndpointProtection',
  'Advanced Endpoint': 'advancedEndpointProtection',
  'Endpoint Security': 'advancedEndpointProtection',
  
  'Onboarded onto SOC': 'onboardedOntoSOC',
  'SOC Monitoring': 'onboardedOntoSOC',
  'SOC': 'onboardedOntoSOC',
  'Security Monitoring': 'onboardedOntoSOC',
  'Onboarded onto SOC / SIEM for logging, detection and monitoring': 'onboardedOntoSOC',
  'SOC/SIEM Monitoring': 'onboardedOntoSOC',
  'SIEM Monitoring': 'onboardedOntoSOC',
  'Security Operations Center': 'onboardedOntoSOC',
  'SOC Integration': 'onboardedOntoSOC',
  
  // Additional fields commonly found in security assessments
  'Decommisioned': 'decommissioned',
  'Decommissioned': 'decommissioned',
  'Asset Status': 'assetStatus',
  'Status': 'assetStatus',
  
  'Proven / tested DR capability within RTO': 'drCapability',
  'Disaster Recovery': 'drCapability',
  'DR Capability': 'drCapability',
  'Business Continuity': 'drCapability',
  'DR Testing': 'drCapability',
  
  'Vulnerabilities remediated per standard (Critical / High rated)': 'vulnerabilityRemediation',
  'Vulnerability Management': 'vulnerabilityRemediation',
  'Patch Management': 'vulnerabilityRemediation',
  'Security Patching': 'vulnerabilityRemediation',
  
  'Application whitelisting': 'applicationWhitelisting',
  'Application Whitelisting': 'applicationWhitelisting',
  'App Whitelisting': 'applicationWhitelisting',
  'Software Whitelisting': 'applicationWhitelisting',
  
  'Penetration testing (last 12 months)': 'penetrationTesting',
  'Penetration Testing': 'penetrationTesting',
  'Pen Testing': 'penetrationTesting',
  'Security Testing': 'penetrationTesting',
  
  // Vendor and Access Management fields
  'Who manages access': 'accessManager',
  'Access Manager': 'accessManager',
  'Who manages RBAC / user entitlements': 'accessManager',
  'User Management': 'accessManager',
  
  'Frequency': 'reviewFrequency',
  'Review Frequency': 'reviewFrequency',
  'Access Review Frequency': 'reviewFrequency',
  
  'Vendor security assessment/questionaire completed...': 'vendorAssessment',
  'Vendor Assessment': 'vendorAssessment',
  'Vendor Security Assessment': 'vendorAssessment',
  'Third Party Assessment': 'vendorAssessment',
  
  'Vendor Contract Link/Location': 'vendorContract',
  'Vendor Contract': 'vendorContract',
  'Contract Location': 'vendorContract',
  
  'Level of access': 'accessLevel',
  'Access Level': 'accessLevel',
  'Vendor Access Level': 'accessLevel',
  
  'Who is in charge to approve the vendors access?': 'accessApprover',
  'Access Approver': 'accessApprover',
  'Vendor Access Approver': 'accessApprover',
  
  'Is there any process to oversight vendor activities...': 'vendorOversight',
  'Vendor Oversight': 'vendorOversight',
  'Third Party Oversight': 'vendorOversight',
  'Vendor Monitoring': 'vendorOversight',
  
  // Additional Access Management fields
  'Privileged access management': 'privilegedAccessManagement',
  'Privileged Access Management': 'privilegedAccessManagement',
  'PAM': 'privilegedAccessManagement',
  'Privileged Access': 'privilegedAccessManagement',
  'Admin Access Management': 'privilegedAccessManagement',
  
  'User revalidation process?': 'userRevalidationProcess',
  'User Revalidation Process': 'userRevalidationProcess',
  'User revalidation process': 'userRevalidationProcess',
  'Access Revalidation': 'userRevalidationProcess',
  'User Access Revalidation': 'userRevalidationProcess',
  'Account Revalidation': 'userRevalidationProcess',
  'Periodic User Review': 'userRevalidationProcess'
};

// Value transformation rules - standardize different value formats
export const VALUE_TRANSFORMS = {
  // YES/NO variations to standardized Yes/No
  'YES': 'Yes',
  'yes': 'Yes',
  'Y': 'Yes',
  'y': 'Yes',
  'TRUE': 'Yes',
  'true': 'Yes',
  'True': 'Yes',
  '1': 'Yes',
  'IMPLEMENTED': 'Yes',
  'implemented': 'Yes',
  'ENABLED': 'Yes',
  'enabled': 'Yes',
  'ACTIVE': 'Yes',
  'active': 'Yes',
  'PRESENT': 'Yes',
  'present': 'Yes',
  'AVAILABLE': 'Yes',
  'available': 'Yes',
  'DEPLOYED': 'Yes',
  'deployed': 'Yes',
  
  'NO': 'No',
  'no': 'No',
  'N': 'No',
  'n': 'No',
  'FALSE': 'No',
  'false': 'No',
  'False': 'No',
  '0': 'No',
  'NOT IMPLEMENTED': 'No',
  'not implemented': 'No',
  'DISABLED': 'No',
  'disabled': 'No',
  'INACTIVE': 'No',
  'inactive': 'No',
  'ABSENT': 'No',
  'absent': 'No',
  'MISSING': 'No',
  'missing': 'No',
  'NOT AVAILABLE': 'No',
  'not available': 'No',
  'NOT DEPLOYED': 'No',
  'not deployed': 'No',
  
  // Handle N/A variations
  'N/A': 'N/A',
  'n/a': 'N/A',
  'NA': 'N/A',
  'na': 'N/A',
  'Not Applicable': 'N/A',
  'not applicable': 'N/A',
  'NOT APPLICABLE': 'N/A',
  'DOES NOT APPLY': 'N/A',
  'does not apply': 'N/A',
  'NOT RELEVANT': 'N/A',
  'not relevant': 'N/A',
  
  // Unknown variations
  'UNKNOWN': 'Unknown',
  'unknown': 'Unknown',
  'Unknown': 'Unknown',
  '?': 'Unknown',
  'TBD': 'Unknown',
  'tbd': 'Unknown',
  'TO BE DETERMINED': 'Unknown',
  'to be determined': 'Unknown',
  'UNCLEAR': 'Unknown',
  'unclear': 'Unknown',
  'UNDETERMINED': 'Unknown',
  'undetermined': 'Unknown',
  
  // Risk Sources standardization (PRD v8 update)
  'INTERNAL': 'Internal',
  'internal': 'Internal',
  'Internal': 'Internal',
  'INSIDE': 'Internal',
  'inside': 'Internal',
  'IN-HOUSE': 'Internal',
  'in-house': 'Internal',
  'INHOUSE': 'Internal',
  'inhouse': 'Internal',
  'INTERNAL USE': 'Internal', // Legacy mapping
  'internal use': 'Internal', // Legacy mapping
  'PUBLIC': 'Internal', // Legacy mapping - public assumes internal system
  'public': 'Internal', // Legacy mapping
  'LOW': 'Internal', // Legacy mapping from old field
  'low': 'Internal', // Legacy mapping
  
  'EXTERNAL': 'External',
  'external': 'External',
  'External': 'External',
  'OUTSIDE': 'External',
  'outside': 'External',
  'THIRD-PARTY': 'External',
  'third-party': 'External',
  'THIRD PARTY': 'External',
  'third party': 'External',
  'VENDOR': 'External',
  'vendor': 'External',
  'SUPPLIER': 'External',
  'supplier': 'External',
  'OUTSOURCED': 'External',
  'outsourced': 'External',
  'CONFIDENTIAL': 'Internal', // Legacy mapping - confidential assumes internal risk
  'confidential': 'Internal', // Legacy mapping
  'RESTRICTED': 'Internal', // Legacy mapping
  'restricted': 'Internal', // Legacy mapping
  'PRIVATE': 'Internal', // Legacy mapping
  'private': 'Internal', // Legacy mapping
  'SENSITIVE': 'Internal', // Legacy mapping
  'sensitive': 'Internal', // Legacy mapping
  'MEDIUM': 'Internal', // Legacy mapping
  'medium': 'Internal', // Legacy mapping
  
  'BOTH': 'Both',
  'both': 'Both',
  'Both': 'Both',
  'MIXED': 'Both',
  'mixed': 'Both',
  'HYBRID': 'Both',
  'hybrid': 'Both',
  'COMBINATION': 'Both',
  'combination': 'Both',
  'INTERNAL AND EXTERNAL': 'Both',
  'internal and external': 'Both',
  'INTERNAL & EXTERNAL': 'Both',
  'internal & external': 'Both',
  'INTERNAL/EXTERNAL': 'Both',
  'internal/external': 'Both',
  'HIGHLY CONFIDENTIAL': 'Internal', // Legacy mapping - highly confidential assumes internal risk
  'highly confidential': 'Internal', // Legacy mapping
  'TOP SECRET': 'Internal', // Legacy mapping
  'top secret': 'Internal', // Legacy mapping
  'SECRET': 'Internal', // Legacy mapping
  'secret': 'Internal', // Legacy mapping
  'STRICTLY CONFIDENTIAL': 'Internal', // Legacy mapping
  'strictly confidential': 'Internal', // Legacy mapping
  'HIGH': 'Internal', // Legacy mapping
  'high': 'Internal', // Legacy mapping
  
  // Criticality level standardization
  'EXTREMELY': '1-Extremely',
  'extremely': '1-Extremely',
  'CRITICAL': '1-Extremely',
  'critical': '1-Extremely',
  'EXTREMELY CRITICAL': '1-Extremely',
  'extremely critical': '1-Extremely',
  '1': '1-Extremely',
  '1 - EXTREMELY': '1-Extremely',
  '1 - extremely': '1-Extremely',
  
  'VERY HIGH': '2-Very High',
  'very high': '2-Very High',
  'HIGH CRITICAL': '2-Very High',
  'high critical': '2-Very High',
  '2': '2-Very High',
  '2 - VERY HIGH': '2-Very High',
  '2 - very high': '2-Very High',
  
  'HIGH': '3-High',
  'high': '3-High',
  '3': '3-High',
  '3 - HIGH': '3-High',
  '3 - high': '3-High',
  
  'MEDIUM': '4-Medium',
  'medium': '4-Medium',
  'MODERATE': '4-Medium',
  'moderate': '4-Medium',
  '4': '4-Medium',
  '4 - MEDIUM': '4-Medium',
  '4 - medium': '4-Medium',
  
  'LOW': '5-Low',
  'low': '5-Low',
  'MINIMAL': '5-Low',
  'minimal': '5-Low',
  '5': '5-Low',
  '5 - LOW': '5-Low',
  '5 - low': '5-Low'
};

/**
 * Automatically map CSV columns to application fields
 * @param {Array} csvHeaders - Array of CSV column headers
 * @returns {Object} Mapping object with CSV header as key and app field as value
 */
export const autoMapColumns = (csvHeaders) => {
  const mapping = {};
  const unmappedColumns = [];
  
  csvHeaders.forEach(header => {
    const trimmedHeader = header.trim();
    const mappedField = CSV_FIELD_MAPPING[trimmedHeader];
    
    if (mappedField) {
      mapping[trimmedHeader] = mappedField;
    } else {
      // Try fuzzy matching for close matches
      const fuzzyMatch = findFuzzyMatch(trimmedHeader, Object.keys(CSV_FIELD_MAPPING));
      if (fuzzyMatch) {
        mapping[trimmedHeader] = CSV_FIELD_MAPPING[fuzzyMatch];
      } else {
        unmappedColumns.push(trimmedHeader);
      }
    }
  });
  
  return {
    mapping,
    unmappedColumns,
    mappedCount: Object.keys(mapping).length,
    totalColumns: csvHeaders.length
  };
};

/**
 * Find fuzzy match for column header (improved similarity check)
 * @param {string} header - Header to match
 * @param {Array} possibleMatches - Array of possible matches
 * @returns {string|null} Best match or null if no good match found
 */
const findFuzzyMatch = (header, possibleMatches) => {
  const headerLower = header.toLowerCase().trim();
  
  // First try exact substring matches
  for (const possible of possibleMatches) {
    const possibleLower = possible.toLowerCase();
    if (headerLower.includes(possibleLower) || possibleLower.includes(headerLower)) {
      return possible;
    }
  }
  
  // Then try word-based matching with lower threshold
  for (const possible of possibleMatches) {
    const possibleLower = possible.toLowerCase();
    
    // Check if header contains key words from possible match
    const headerWords = headerLower.split(/[\s_\-\/\(\)\,\.]+/).filter(w => w.length > 2);
    const possibleWords = possibleLower.split(/[\s_\-\/\(\)\,\.]+/).filter(w => w.length > 2);
    
    if (headerWords.length === 0 || possibleWords.length === 0) continue;
    
    // Count matching words
    const matchingWords = headerWords.filter(word => 
      possibleWords.some(pWord => 
        pWord.includes(word) || word.includes(pWord) ||
        // Check for common abbreviations
        (word === 'sso' && pWord.includes('sign')) ||
        (word === 'rbac' && pWord.includes('access')) ||
        (word === '2fa' && pWord.includes('factor')) ||
        (word === 'mfa' && pWord.includes('factor')) ||
        (word === 'soc' && pWord.includes('security')) ||
        (word === 'edr' && pWord.includes('endpoint')) ||
        (word === 'aep' && pWord.includes('endpoint'))
      )
    );
    
    // Lower threshold from 70% to 40% for better matching
    const matchRatio = matchingWords.length / Math.max(headerWords.length, possibleWords.length);
    if (matchRatio >= 0.4) {
      return possible;
    }
  }
  
  return null;
};

/**
 * Extract asset name from CSV row with multiple fallback methods
 * @param {Object} csvRow - Raw CSV row data
 * @param {Object} columnMapping - Mapping between CSV columns and app fields
 * @param {number} rowIndex - Row index for generating names (0-based)
 * @param {Object} clientInfo - Optional client information
 * @returns {string} Extracted or generated asset name
 */
const extractAssetName = (csvRow, columnMapping, rowIndex, clientInfo = null) => {
  console.log(`DEBUG - Row ${rowIndex + 1} - Extracting asset name...`);
  console.log('DEBUG - CSV Row keys:', Object.keys(csvRow));
  console.log('DEBUG - Column Mapping:', columnMapping);
  
  // Method 1: Try mapped column
  const mappedColumn = Object.keys(columnMapping).find(col => 
    columnMapping[col] === 'assetName'
  );
  
  if (mappedColumn && csvRow[mappedColumn] !== undefined) {
    const value = String(csvRow[mappedColumn] || '').trim();
    console.log(`DEBUG - Method 1 (Mapped): Column "${mappedColumn}" = "${value}"`);
    if (value && value !== '') {
      console.log(`DEBUG - SUCCESS: Using mapped value "${value}"`);
      return value;
    }
  }
  
  // Method 2: Try direct column access with known patterns
  const possibleColumns = [
    'Application / system name',
    'Application/system name',
    'Application / System name',
    'Application/System name',
    'Application / System Name',
    'Application/System Name',
    'Application name',
    'Application Name',
    'System name',
    'System Name',
    'Asset name',
    'Asset Name',
    'Name',
    'APPLICATION / SYSTEM NAME',
    'APPLICATION/SYSTEM NAME',
    'APPLICATION NAME',
    'SYSTEM NAME'
  ];
  
  console.log('DEBUG - Method 2: Trying direct column access...');
  for (const colName of possibleColumns) {
    if (csvRow[colName] !== undefined) {
      const value = String(csvRow[colName] || '').trim();
      console.log(`DEBUG - Checking column "${colName}" = "${value}"`);
      if (value && value !== '') {
        console.log(`DEBUG - SUCCESS: Found asset name via direct access: "${colName}" = "${value}"`);
        return value;
      }
    }
  }
  
  // Method 3: Try case-insensitive fuzzy search
  console.log('DEBUG - Method 3: Trying fuzzy search...');
  const csvKeys = Object.keys(csvRow);
  const applicationCol = csvKeys.find(key => {
    const lowerKey = key.toLowerCase();
    return lowerKey.includes('application') && 
           (lowerKey.includes('name') || lowerKey.includes('system'));
  });
  
  if (applicationCol && csvRow[applicationCol] !== undefined) {
    const value = String(csvRow[applicationCol] || '').trim();
    console.log(`DEBUG - Fuzzy found column "${applicationCol}" = "${value}"`);
    if (value && value !== '') {
      console.log(`DEBUG - SUCCESS: Found asset name via fuzzy search: "${applicationCol}" = "${value}"`);
      return value;
    }
  }
  
  // Method 4: Try any column that might contain application/system names
  console.log('DEBUG - Method 4: Trying broad search...');
  const nameColumns = csvKeys.filter(key => {
    const lowerKey = key.toLowerCase();
    return (lowerKey.includes('name') || lowerKey.includes('title') || lowerKey.includes('system')) &&
           !lowerKey.includes('owner') && 
           !lowerKey.includes('contact');
  });
  
  for (const colName of nameColumns) {
    if (csvRow[colName] !== undefined) {
      const value = String(csvRow[colName] || '').trim();
      console.log(`DEBUG - Broad search column "${colName}" = "${value}"`);
      if (value && value !== '' && value.length > 1) {
        console.log(`DEBUG - SUCCESS: Found asset name via broad search: "${colName}" = "${value}"`);
        return value;
      }
    }
  }
  
  // Method 5: Last resort - generate name with client prefix
  const clientPrefix = clientInfo?.name ? `${clientInfo.name} - ` : '';
  const generatedName = `${clientPrefix}Asset ${rowIndex + 1}`;
  console.warn(`DEBUG - FALLBACK: No asset name found in row ${rowIndex + 1}, generating "${generatedName}"`);
  console.log('DEBUG - Available CSV columns and values:');
  Object.entries(csvRow).forEach(([key, value]) => {
    console.log(`  "${key}": "${value}"`);
  });
  
  return generatedName;
};

/**
 * Generate simple, user-friendly asset names for empty fields
 * @param {string} rawValue - Original asset name value from CSV
 * @param {number} rowIndex - Row index for sequential numbering (0-based)
 * @param {Object} clientInfo - Optional client information for prefixing
 * @returns {string} Generated or original asset name
 */
const generateAssetName = (rawValue, rowIndex, clientInfo = null) => {
  // If we have a valid name, use it
  if (rawValue && rawValue.trim() !== '') {
    return rawValue.trim();
  }
  
  // Generate simple, user-friendly name for empty values
  const clientPrefix = clientInfo?.name ? `${clientInfo.name} - ` : '';
  const assetNumber = rowIndex + 1; // Convert to 1-based indexing for user display
  
  return `${clientPrefix}Asset ${assetNumber}`;
};

/**
 * Transform CSV row data to application format
 * @param {Object} csvRow - Raw CSV row data
 * @param {Object} columnMapping - Mapping between CSV columns and app fields
 * @param {number} rowIndex - Row index for generating unique names (optional)
 * @param {Object} clientInfo - Optional client information for enhanced naming
 * @returns {Object} Transformed data in application format
 */
export const transformRowData = (csvRow, columnMapping, rowIndex = 0, clientInfo = null) => {
  console.log(`\n=== TRANSFORMING ROW ${rowIndex + 1} ===`);
  console.log('DEBUG - Raw CSV Row:', csvRow);
  console.log('DEBUG - Column Mapping:', columnMapping);
  console.log('DEBUG - Available CSV columns:', Object.keys(csvRow));
  
  const transformedData = {};
  
  // Initialize with default structure (updated for streamlined fields)
  transformedData.basicInfo = {
    name: '',
    description: '',
    location: ''
  };
  transformedData.ownerInfo = {
    ownerDepartment: PLACEHOLDER_VALUES.TEXT,
    ownerName: PLACEHOLDER_VALUES.TEXT,
    ownerEmail: PLACEHOLDER_VALUES.EMAIL
  };
  transformedData.securityInfo = {
    riskSources: 'Internal',
    criticalityClassification: '4-Medium'
  };
  transformedData.keyControls = {
    ssoImplemented: 'No',
    twoFactorAuth: 'No',
    dataEncryption: 'No',
    backupsImplemented: 'No',
    networkSegmentation: 'No',
    userAccessReviews: 'No'
  };
  transformedData.dataInfo = {
    containsSensitiveData: 'None'
  };
  
  // Extract asset name using enhanced logic
  const extractedAssetName = extractAssetName(csvRow, columnMapping, rowIndex, clientInfo);
  transformedData.basicInfo.name = extractedAssetName;
  console.log(`DEBUG - Final asset name for row ${rowIndex + 1}: "${extractedAssetName}"`);
  
  // Handle consolidated data classification
  const consolidatedDataType = consolidateDataClassification(csvRow);
  transformedData.dataInfo.containsSensitiveData = consolidatedDataType;
  console.log(`DEBUG - Consolidated data classification: "${consolidatedDataType}"`);
  
  // Process each mapped column for other fields
  Object.entries(columnMapping).forEach(([csvColumn, appField]) => {
    const rawValue = csvRow[csvColumn];
    
    // Skip asset name as we handled it separately above
    if (appField === 'assetName') {
      return;
    }
    
    // Skip consolidated data classification as we handled it above
    if (appField === 'containsSensitiveData') {
      return;
    }
    
    // Skip legacy data classification fields if we have new consolidated field
    if (['customerPII', 'employeePII', 'sensitivePII', 'boardInfo', 'executiveInfo', 'financialInfo', 'otherConfidential'].includes(appField)) {
      console.log(`DEBUG - Skipping legacy data field: ${appField}`);
      return;
    }
    
    // Skip removed key control fields 
    if (['rbacImplemented', 'advancedEndpointProtection', 'onboardedOntoSOC'].includes(appField)) {
      console.log(`DEBUG - Skipping removed control field: ${appField}`);
      return;
    }
    
    let transformedValue = transformValue(rawValue, appField);
    
    // For owner info fields, use appropriate placeholders if value is empty
    if (['ownerDepartment', 'ownerName'].includes(appField)) {
      if (!transformedValue || transformedValue.trim() === '') {
        transformedValue = PLACEHOLDER_VALUES.TEXT;
      }
    } else if (appField === 'ownerEmail') {
      if (!transformedValue || transformedValue.trim() === '') {
        transformedValue = PLACEHOLDER_VALUES.EMAIL;
      }
    }
    
    // Map to appropriate section based on field name
    setNestedValue(transformedData, appField, transformedValue);
  });
  
  console.log(`=== COMPLETED ROW ${rowIndex + 1} ===\n`);
  return transformedData;
};

/**
 * Process criticality classification values with enhanced normalization
 * @param {string} value - Input criticality value
 * @returns {string} Processed criticality value
 */
const processCriticalityValue = (value) => {
  if (!value) return '4-Medium'; // Default value
  
  let processed = String(value).trim();
  
  // Handle various space patterns in criticality values using regex
  processed = processed.replace(/^(\d+)\s*-\s*(.+)$/i, '$1-$2');
  
  // Handle cases without numbers (just text) - map to appropriate levels
  const textMappings = {
    'critical': '1-Extremely',
    'extremely': '1-Extremely',
    'very high': '2-Very High',
    'veryhigh': '2-Very High',
    'high': '3-High',
    'medium': '4-Medium',
    'moderate': '4-Medium',
    'low': '5-Low',
    'minimal': '5-Low'
  };
  
  const lowerProcessed = processed.toLowerCase();
  if (textMappings[lowerProcessed]) {
    return textMappings[lowerProcessed];
  }
  
  return processed;
};

/**
 * Transform individual value based on field type and transformation rules
 * @param {string} value - Raw value from CSV
 * @param {string} fieldName - Name of the application field
 * @returns {string} Transformed value
 */
const transformValue = (value, fieldName) => {
  // Handle completely empty values
  if (!value || value === '' || String(value).trim() === '') {
    return '';
  }
  
  let stringValue = String(value).trim();
  
  // Apply field-specific processing
  if (fieldName === 'criticalityClassification') {
    stringValue = processCriticalityValue(stringValue);
  }
  
  // Apply value corrections first
  const correctedValue = correctValue(stringValue);
  
  // Apply value transformations
  const transformedValue = VALUE_TRANSFORMS[correctedValue] || correctedValue;
  
  // Additional field-specific transformations
  if (fieldName === 'ownerEmail') {
    return transformedValue.toLowerCase();
  }
  
  // Handle boolean-like fields that aren't mapped in VALUE_TRANSFORMS
  if (['boardInfo', 'executiveInfo', 'financialInfo', 'otherConfidential', 
       'customerPII', 'employeePII', 'sensitivePII'].includes(fieldName)) {
    // For data classification fields, default unknown values to 'Unknown'
    if (!['Yes', 'No', 'Unknown'].includes(transformedValue)) {
      return 'Unknown';
    }
  }
  
  if (['ssoImplemented', 'rbacImplemented', 'twoFactorAuth', 'networkSegmentation',
       'dataEncryption', 'userAccessReviews', 'advancedEndpointProtection',
       'onboardedOntoSOC', 'backupsImplemented', 'privilegedAccessManagement'].includes(fieldName)) {
    // For key controls and access management, default unknown values to 'N/A'
    if (!['Yes', 'No', 'N/A'].includes(transformedValue)) {
      return 'N/A';
    }
  }

  // Handle frequency and process fields
  if (['userRevalidationProcess'].includes(fieldName)) {
    // For process fields, keep as-is or default to 'Unknown'
    if (!transformedValue || transformedValue === '') {
      return 'Unknown';
    }
  }
  
  return transformedValue;
};

/**
 * Set nested value in object based on field mapping
 * @param {Object} obj - Target object
 * @param {string} fieldName - Field name to set
 * @param {string} value - Value to set
 */
const setNestedValue = (obj, fieldName, value) => {
  // Map field names to their respective sections - updated for Risk Sources (PRD v8)
  const fieldSectionMap = {
    // Basic Info
    assetName: ['basicInfo', 'name'],
    assetDescription: ['basicInfo', 'description'],
    assetLocation: ['basicInfo', 'location'],
    
    // Owner Info
    ownerDepartment: ['ownerInfo', 'ownerDepartment'],
    ownerName: ['ownerInfo', 'ownerName'],
    ownerEmail: ['ownerInfo', 'ownerEmail'],
    
    // Security Info (updated for Risk Sources)
    riskSources: ['securityInfo', 'riskSources'],
    criticalityClassification: ['securityInfo', 'criticalityClassification'],
    
    // Streamlined Key Controls (only 6 controls)
    ssoImplemented: ['keyControls', 'ssoImplemented'],
    twoFactorAuth: ['keyControls', 'twoFactorAuth'],
    dataEncryption: ['keyControls', 'dataEncryption'],
    backupsImplemented: ['keyControls', 'backupsImplemented'],
    networkSegmentation: ['keyControls', 'networkSegmentation'],
    userAccessReviews: ['keyControls', 'userAccessReviews'],
    
    // New consolidated data classification
    containsSensitiveData: ['dataInfo', 'containsSensitiveData'],
    
    // Legacy fields (for backward compatibility during migration - not stored in final structure)
    securityClassification: null, // Legacy field - will be migrated to riskSources
    boardInfo: null, // Will be consolidated into containsSensitiveData
    executiveInfo: null, // Will be consolidated into containsSensitiveData
    financialInfo: null, // Will be consolidated into containsSensitiveData
    otherConfidential: null, // Will be consolidated into containsSensitiveData
    customerPII: null, // Will be consolidated into containsSensitiveData
    employeePII: null, // Will be consolidated into containsSensitiveData
    sensitivePII: null, // Will be consolidated into containsSensitiveData
    rbacImplemented: null, // Removed from streamlined structure
    advancedEndpointProtection: null, // Removed from streamlined structure
    onboardedOntoSOC: null // Removed from streamlined structure
    
    // Additional fields (these won't be stored in the main structure but won't cause errors)
    // decommissioned, drCapability, vulnerabilityRemediation, etc. can be handled separately if needed
  };
  
  const path = fieldSectionMap[fieldName];
  if (path && path.length === 2) {
    const [section, field] = path;
    if (obj[section]) {
      obj[section][field] = value;
    }
  } else if (fieldSectionMap.hasOwnProperty(fieldName) && fieldSectionMap[fieldName] === null) {
    // Legacy field that should not be stored - log for debugging but don't store
    console.log(`DEBUG - Skipping legacy field: ${fieldName} = ${value}`);
  }
  // If field is not in the standard structure, just ignore it for now
  // This prevents errors for unmapped fields that may be recognized but not used
};

/**
 * Get all available field mappings for manual mapping interface - updated for streamlined structure
 * @returns {Object} Object with categories and their available fields
 */
export const getAvailableFields = () => {
  return {
    'Basic Information': [
      { value: 'assetName', label: 'Asset Name' },
      { value: 'assetDescription', label: 'Description' },
      { value: 'assetLocation', label: 'Location' }
    ],
    'Owner Information': [
      { value: 'ownerDepartment', label: 'Owner Department' },
      { value: 'ownerName', label: 'Owner Name' },
      { value: 'ownerEmail', label: 'Owner Email' }
    ],
    'Risk Classification': [
      { value: 'riskSources', label: 'Risk Sources' },
      { value: 'criticalityClassification', label: 'Criticality Classification' }
    ],
    'Data Classification': [
      { value: 'containsSensitiveData', label: 'Contains Sensitive Data' }
    ],
    'Key Controls': [
      { value: 'ssoImplemented', label: 'SSO' },
      { value: 'twoFactorAuth', label: '2FA' },
      { value: 'dataEncryption', label: 'Data Encryption' },
      { value: 'backupsImplemented', label: 'Backups' },
      { value: 'networkSegmentation', label: 'Network Segmentation' },
      { value: 'userAccessReviews', label: 'User Access Reviews' }
    ],
    
    // Legacy fields (for backward compatibility during import)
    'Legacy Data Fields (Deprecated)': [
      { value: 'boardInfo', label: 'Board Information (Legacy)' },
      { value: 'executiveInfo', label: 'Executive Information (Legacy)' },
      { value: 'financialInfo', label: 'Financial Information (Legacy)' },
      { value: 'otherConfidential', label: 'Other Confidential Information (Legacy)' },
      { value: 'customerPII', label: 'Customer PII (Legacy)' },
      { value: 'employeePII', label: 'Employee PII (Legacy)' },
      { value: 'sensitivePII', label: 'Sensitive Employee PII (Legacy)' }
    ],
    'Legacy Control Fields (Deprecated)': [
      { value: 'rbacImplemented', label: 'RBAC (Legacy)' },
      { value: 'advancedEndpointProtection', label: 'Advanced Endpoint Protection (Legacy)' },
      { value: 'onboardedOntoSOC', label: 'Onboarded onto SOC (Legacy)' }
    ],
    'Access Management': [
      { value: 'privilegedAccessManagement', label: 'Privileged Access Management' },
      { value: 'userRevalidationProcess', label: 'User Revalidation Process' },
      { value: 'accessManager', label: 'Access Manager' },
      { value: 'reviewFrequency', label: 'Review Frequency' },
      { value: 'accessLevel', label: 'Access Level' },
      { value: 'accessApprover', label: 'Access Approver' }
    ],
    'Vendor Management': [
      { value: 'vendorAssessment', label: 'Vendor Security Assessment' },
      { value: 'vendorContract', label: 'Vendor Contract' },
      { value: 'vendorOversight', label: 'Vendor Oversight' }
    ]
  };
};

/**
 * Get mapping confidence score
 * @param {Object} mappingResult - Result from autoMapColumns
 * @returns {Object} Confidence score and recommendations
 */
export const getMappingConfidence = (mappingResult) => {
  const { mappedCount, totalColumns, unmappedColumns } = mappingResult;
  const mappingPercentage = (mappedCount / totalColumns) * 100;
  
  let confidence = 'high';
  let recommendations = [];
  
  if (mappingPercentage < 50) {
    confidence = 'low';
    recommendations.push('Consider manually mapping unmapped columns');
    recommendations.push('Verify that your CSV uses standard column names');
  } else if (mappingPercentage < 80) {
    confidence = 'medium';
    recommendations.push('Review unmapped columns for important data');
  }
  
  if (unmappedColumns.length > 0) {
    recommendations.push(`${unmappedColumns.length} columns could not be automatically mapped`);
  }
  
  return {
    score: mappingPercentage,
    level: confidence,
    recommendations,
    mappedCount,
    totalColumns,
    unmappedColumns
  };
};

/**
 * Create manual mapping interface for unmapped columns
 * @param {Array} unmappedColumns - Array of unmapped column names
 * @returns {Object} Manual mapping configuration
 */
export const createManualMappingConfig = (unmappedColumns) => {
  const availableFields = getAvailableFields();
  const flatFields = [];
  
  // Flatten available fields for dropdown
  Object.entries(availableFields).forEach(([category, fields]) => {
    fields.forEach(field => {
      flatFields.push({
        value: field.value,
        label: `${category}: ${field.label}`,
        category: category
      });
    });
  });
  
  return {
    unmappedColumns: unmappedColumns.map(column => ({
      csvColumn: column,
      suggestedMapping: findBestSuggestion(column, flatFields),
      selectedMapping: null
    })),
    availableFields: flatFields
  };
};

/**
 * Find best mapping suggestion for a column
 * @param {string} columnName - Column name to find suggestion for
 * @param {Array} availableFields - Available fields to map to
 * @returns {string|null} Best suggested field or null
 */
const findBestSuggestion = (columnName, availableFields) => {
  const columnLower = columnName.toLowerCase();
  
  // Look for partial matches in field labels
  for (const field of availableFields) {
    const labelLower = field.label.toLowerCase();
    const words = columnLower.split(/[\s_\-\/\(\)\,\.]+/).filter(w => w.length > 2);
    
    const matchingWords = words.filter(word => labelLower.includes(word));
    if (matchingWords.length > 0) {
      return field.value;
    }
  }
  
  return null;
};

/**
 * Transform sensitive data classification value from legacy fields to new consolidated field
 * @param {string} value - Input sensitive data value
 * @returns {string} Transformed sensitive data value
 */
const transformSensitiveDataValue = (value) => {
  if (!value) return 'None';
  
  const normalizedValue = value.toLowerCase().trim();
  
  // Map common variations to standard values
  const dataTypeMap = {
    'customer': 'Customer PII',
    'customer pii': 'Customer PII',
    'customer data': 'Customer PII', 
    'personal': 'Customer PII',
    'customer information': 'Customer PII',
    'client data': 'Customer PII',
    'client information': 'Customer PII',
    
    'employee': 'Employee PII',
    'employee pii': 'Employee PII',
    'staff': 'Employee PII',
    'personnel': 'Employee PII',
    'employee data': 'Employee PII',
    'employee information': 'Employee PII',
    'staff data': 'Employee PII',
    'personnel information': 'Employee PII',
    'sensitive employee pii': 'Employee PII',
    'sensitive pii': 'Employee PII',
    
    'financial': 'Financial Information',
    'finance': 'Financial Information',
    'accounting': 'Financial Information',
    'budget': 'Financial Information',
    'financial data': 'Financial Information',
    'financial information': 'Financial Information',
    'accounting data': 'Financial Information',
    'budget information': 'Financial Information',
    'financial records': 'Financial Information',
    
    'executive': 'Executive Information',
    'board': 'Executive Information',
    'c-level': 'Executive Information',
    'management': 'Executive Information',
    'executive information': 'Executive Information',
    'board information': 'Executive Information',
    'executive data': 'Executive Information',
    'board data': 'Executive Information',
    'senior management': 'Executive Information',
    'c-level information': 'Executive Information',
    
    'multiple': 'Multiple Types',
    'various': 'Multiple Types',
    'mixed': 'Multiple Types',
    'several': 'Multiple Types',
    'combination': 'Multiple Types',
    'multiple types': 'Multiple Types',
    'various types': 'Multiple Types',
    'mixed types': 'Multiple Types',
    
    'none': 'None',
    'no': 'None',
    'n/a': 'None',
    'not applicable': 'None',
    'nothing': 'None'
  };
  
  return dataTypeMap[normalizedValue] || value;
};

/**
 * Consolidate legacy data classification fields into new structure
 * @param {Object} csvRow - Raw CSV row data
 * @returns {string} Consolidated sensitive data type
 */
const consolidateDataClassification = (csvRow) => {
  // Check if new field is already present
  const directValue = csvRow['Contains Sensitive Data'] || 
                     csvRow['Sensitive Data Type'] || 
                     csvRow['Data Types'] ||
                     csvRow['Information Types'];
  
  if (directValue && directValue.trim() !== '') {
    return transformSensitiveDataValue(directValue);
  }
  
  // Consolidate from legacy fields
  const hasCustomerPII = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Customer PII'] || '').toLowerCase().trim()
  );
  const hasEmployeePII = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Employee PII'] || '').toLowerCase().trim()
  );
  const hasSensitivePII = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Sensitive Employee PII'] || '').toLowerCase().trim()
  );
  const hasFinancial = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Financial Information'] || '').toLowerCase().trim()
  );
  const hasExecutive = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Executive Information'] || '').toLowerCase().trim()
  );
  const hasBoard = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Board Information'] || '').toLowerCase().trim()
  );
  const hasOtherConfidential = ['yes', 'true', '1', 'implemented', 'present'].includes(
    String(csvRow['Other Confidential Information'] || '').toLowerCase().trim()
  );
  
  const dataTypes = [];
  if (hasCustomerPII) dataTypes.push('Customer PII');
  if (hasEmployeePII || hasSensitivePII) dataTypes.push('Employee PII'); 
  if (hasFinancial) dataTypes.push('Financial Information');
  if (hasExecutive || hasBoard) dataTypes.push('Executive Information');
  
  if (dataTypes.length === 0 && !hasOtherConfidential) {
    return 'None';
  } else if (dataTypes.length === 1 && !hasOtherConfidential) {
    return dataTypes[0];
  } else {
    return 'Multiple Types';
  }
}; 